import equal from "fast-deep-equal";
import { useCallback, useMemo, useRef, useState } from "react";
import type { Layout } from "react-grid-layout";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper.ts";
import type { ApiError } from "@entrilia/shared/api/types";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext.ts";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging.ts";
import adminApi from "../../../../api/adminApi";
import biApi from "../../../../api/biApi.ts";
import type {
  Dashboard,
  GetDashboardResult,
  Tile,
  TileLocation,
  TileSmartView,
  Widgets,
} from "../../../../api/types/dashboardsTypes.ts";
import { WidgetType } from "../../../../api/types/dashboardsTypes.ts";
import useClientContext from "../../../../context/useClientContext.ts";
import type { ActiveFilter, DashboardFilter, WidgetFilter } from "../dashboard.types.ts";
import type { TileObject } from "../models/TileObject";
import { getEditTileUrl } from "../utils/dashboardUrls.ts";
import { getFilterWidgetValues } from "../widgets/filter/getFilterWidgetValues.ts";
import { getFilterWidgetDescendantLevels, type FilterWidgetSummary } from "../widgets/filter/parentFilterOptions.ts";
import { runFilterRevalidationCascade } from "../widgets/filter/revalidateFilterCascade.ts";

const getBusyWidgetIds = (runs: Map<string, { widgetIds: string[] }>): string[] => {
  const ids = new Set<string>();
  runs.forEach((run) => run.widgetIds.forEach((widgetId) => ids.add(widgetId)));
  return [...ids];
};

const useDashboardState = (id: string) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { clientCode } = useClientContext();
  const [tileObjects, setTileObjects] = useState<TileObject[]>([]);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [dashboard, setDashboard] = useState<Dashboard>();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<ApiError>();
  const [widgetFilters, setWidgetFilters] = useState<WidgetFilter[]>([]);
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | undefined>(undefined);
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [revalidatingWidgetIds, setRevalidatingWidgetIds] = useState<string[]>([]);
  const cascadeRunsRef = useRef(new Map<string, { controller: AbortController; widgetIds: string[] }>());

  const dashboardLoaded = useMemo(() => {
    return tileObjects.every((t) => t.isLoaded);
  }, [tileObjects]);

  const createEditTileUrl = useCallback(
    (tileId: string) => {
      return getEditTileUrl(clientCode, dashboard?.dashboardId ?? "", tileId);
    },
    [clientCode, dashboard?.dashboardId]
  );
  const dashboardFilters = useMemo((): DashboardFilter[] => {
    const filters = widgetFilters.reduce<DashboardFilter[]>((acc, f) => {
      const existingFilter = acc.find((f2) => f2.id === f.id);
      if (existingFilter) {
        existingFilter.widgets.push(f.widgetId);
        return acc;
      }
      return [
        ...acc,
        {
          id: f.id,
          caption: f.caption,
          dataType: f.dataType,
          widgets: [f.widgetId],
          options: f.options,
          dataSetId: f.dataSetId,
          availableFields: f.availableFields,
          isMultiBook: f.isMultiBook,
          books: f.books,
          displayField: f.displayField,
          valueField: f.valueField,
          component: {
            type: "dropdown",
            singleSelect: true,
            showSelectAll: false,
            defaultValue: [],
          },
        },
      ];
    }, [] as DashboardFilter[]);

    return filters;
  }, [widgetFilters]);

  const onDashboardLoaded = useCallback((data: Dashboard) => {
    setDashboard(data);
    setTileObjects(
      data.tiles?.map(
        (tile: Tile): TileObject =>
          ({
            id: tile.tileId,
            isInitialized: true,
            isLoaded: false,
            item: tile,
            initializationError: undefined,
          }) as TileObject
      ) ?? []
    );
  }, []);

  const getDashboard = useCallback(() => adminApi.getDashboard(id), [id]);
  const [, loadingError, { isFetching: isLoading }] = useFetch(getDashboard, {
    onSetData: (data: GetDashboardResult) => onDashboardLoaded(data.dashboard),
  });

  const removedTiles = useMemo(() => {
    if (!dashboard) {
      return [];
    }
    return dashboard.tiles?.filter((t) => !tileObjects.some((o) => o.id === t.tileId)) ?? [];
  }, [dashboard, tileObjects]);

  const saveDashboard = useCallback(async () => {
    if (!dashboard) {
      return;
    }
    const updatedDashboard: Dashboard = { ...dashboard, isDraft: false, tiles: tileObjects.map((t) => t.item) };
    try {
      const response = await adminApi.updateDashboard(updatedDashboard.dashboardId, updatedDashboard);
      if (response.success && response.data?.dashboard) {
        onDashboardLoaded(response.data.dashboard);
      }
    } catch (error) {
      setSaveError(error as ApiError);
    }
  }, [dashboard, tileObjects, onDashboardLoaded]);

  const saveDraftedWidgets = useCallback(async () => {
    const smartViewDrafts = tileObjects
      .filter((t) => t.item.widget.type === WidgetType.SmartView && t.item.widget.isDraft)
      .map((t) => t.item as TileSmartView);
    if (smartViewDrafts.length === 0) {
      return;
    }

    try {
      const reportIds = smartViewDrafts.map((t) => t.widget.reportId);
      await biApi.dashboardReports.undraftReports(reportIds);
      setTileObjects((prev: TileObject[]) =>
        prev.map((t: TileObject) => {
          if (smartViewDrafts.some((s) => s.tileId === t.item.tileId)) {
            t.item.widget.isDraft = false;
            return t;
          }
          return t;
        })
      );
    } catch (error) {
      setSaveError(error as ApiError);
    }
  }, [tileObjects]);

  const deleteRemovedWidgetReports = useCallback(async () => {
    try {
      if (removedTiles.length === 0) {
        return;
      }
      const removedReports = removedTiles
        .filter((t): t is TileSmartView => t.widget.type === WidgetType.SmartView)
        .map((t) => ({ clientCode, reportId: t.widget.reportId }));
      await biApi.dashboardReports.deleteReports(removedReports);
    } catch (error) {
      logError(error, "Failed to delete removed widget reports");
    }
  }, [removedTiles, clientCode]);

  const save = useCallback(() => {
    setIsSaving(true);
    Promise.all([saveDashboard(), saveDraftedWidgets(), deleteRemovedWidgetReports()]).finally(() => {
      setIsSaving(false);
      sendNotification("Dashboard saved successfully");
    });
  }, [saveDashboard, saveDraftedWidgets, deleteRemovedWidgetReports, sendNotification]);

  const saveDashboardName = useCallback(
    async (name: string) => {
      if (!dashboard || dashboard.nameUserInput === name) {
        return;
      }

      setIsSaving(true);
      try {
        const [response, error] = await withErrorHandling(adminApi.updateDashboard)(dashboard.dashboardId, {
          ...dashboard,
          nameUserInput: name,
        });
        if (error) {
          logError(error, "[useDashboardState] saveDashboardName");
          sendNotificationError("Failed to save dashboard name");
          throw error;
        }

        setDashboard(response.dashboard);
        sendNotification("Dashboard name saved successfully");
      } finally {
        setIsSaving(false);
      }
    },
    [dashboard, sendNotification, sendNotificationError]
  );

  const addNewTile = useCallback((tile: TileObject) => {
    setTileObjects((prev: TileObject[]) => [...prev, tile]);
  }, []);

  const updateTile = useCallback((tile: Partial<TileObject>) => {
    setTileObjects((prev: TileObject[]) =>
      prev.map((t: TileObject) => (t.id === tile.id ? ({ ...t, ...tile } as TileObject) : t))
    );
  }, []);

  const updateTileWidget = useCallback((tileId: string, widget: Partial<Widgets>) => {
    setTileObjects((prev: TileObject[]) =>
      prev.map((t: TileObject) =>
        t.id === tileId ? ({ ...t, item: { ...t.item, widget: { ...t.item.widget, ...widget } } } as TileObject) : t
      )
    );
  }, []);

  const updateTileLayout = useCallback((layout: Layout) => {
    setTileObjects((prev: TileObject[]) =>
      prev.map((tile: TileObject) => {
        const location = layout.find((l) => l.i === tile.id);
        if (!location) {
          return tile;
        }

        const newLocation: TileLocation = {
          row: location.y,
          column: location.x,
          height: location.h,
          width: location.w,
          isStatic: location.static,
          isDraggable: location.isDraggable,
          isResizable: location.isResizable,
        };

        const newTile: TileObject = {
          ...tile,
          item: {
            ...tile.item,
            location: newLocation,
          },
        } as TileObject;

        return newTile;
      })
    );
  }, []);

  const deleteTile = useCallback((tileId: string) => {
    setTileObjects((prev: TileObject[]) =>
      prev
        .filter((t: TileObject) => t.id !== tileId)
        .map<TileObject>((t: TileObject) => {
          const tileWidget = t.item.widget;
          if (tileWidget.type !== WidgetType.Filter || tileWidget.parentWidgetId !== tileId) {
            return t;
          }
          return { ...t, item: { ...t.item, widget: { ...tileWidget, parentWidgetId: null } } } as TileObject;
        })
    );
    setActiveFilters((prev: ActiveFilter[]) => prev.filter((f) => f.widgetId !== tileId));
    setWidgetFilters((prev: WidgetFilter[]) => prev.filter((f) => f.widgetId !== tileId));
  }, []);

  const updateWidget = useCallback((widgetId: string, properties: Partial<Widgets>) => {
    setTileObjects((prev: TileObject[]) =>
      prev.map<TileObject>((t: TileObject) => {
        if (t.id === widgetId) {
          return {
            ...t,
            item: {
              ...t.item,
              widget: { ...t.item.widget, ...properties },
            },
          } as TileObject;
        }
        return t;
      })
    );
  }, []);

  // A widget registers before its report run resolves, so re-registration must refresh the metadata it carries.
  const addFilters = useCallback((filters: WidgetFilter[]) => {
    setWidgetFilters((prev: WidgetFilter[]) => {
      let changed = false;
      const updated = prev.map((existing) => {
        const next = filters.find((f) => f.id === existing.id && f.widgetId === existing.widgetId);
        if (next === undefined || equal(next, existing)) {
          return existing;
        }
        changed = true;
        return next;
      });
      const added = filters.filter((f) => !prev.some((f2) => f2.id === f.id && f2.widgetId === f.widgetId));
      return changed || added.length > 0 ? [...updated, ...added] : prev;
    });
  }, []);

  const setActiveFilter = useCallback((filter: ActiveFilter) => {
    setActiveFilters((prev: ActiveFilter[]) => {
      const existingFilter = prev.find((f) => f.id === filter.id && f.widgetId === filter.widgetId);
      if (existingFilter) {
        existingFilter.values = filter.values;
        existingFilter.applyToWidgets = filter.applyToWidgets;
        return [...prev];
      }
      return [...prev, filter];
    });
  }, []);

  const removeActiveFilter = useCallback((filterId: string | undefined, widgetId: string) => {
    setActiveFilters((prev: ActiveFilter[]) =>
      prev.filter((f) => {
        if (filterId !== undefined) {
          return f.id !== filterId && f.widgetId !== widgetId;
        }
        return f.widgetId !== widgetId;
      })
    );
  }, []);

  const filterWidgets = useMemo(
    (): FilterWidgetSummary[] =>
      tileObjects
        .map((t) => t.item.widget)
        .filter((w) => w.type === WidgetType.Filter)
        .map((w) => ({
          widgetId: w.widgetId,
          name: w.nameUserInput,
          dashboardFilterId: w.dashboardFilterId,
          dataType: dashboardFilters.find((f) => f.id === w.dashboardFilterId)?.dataType,
          parentWidgetId: w.parentWidgetId,
        })),
    [dashboardFilters, tileObjects]
  );

  const runFilterCascade = useCallback(
    async (
      rootWidgetId: string,
      levels: FilterWidgetSummary[][],
      widgets: FilterWidgetSummary[],
      overrides: Map<string, unknown[]>
    ) => {
      const busyWidgetIds = levels.flat().map((w) => w.widgetId);
      const affectedWidgetIds = new Set([rootWidgetId, ...busyWidgetIds]);
      cascadeRunsRef.current.forEach((run, runRootId) => {
        if (affectedWidgetIds.has(runRootId) || run.widgetIds.some((id) => affectedWidgetIds.has(id))) {
          run.controller.abort();
          cascadeRunsRef.current.delete(runRootId);
        }
      });
      const controller = new AbortController();
      cascadeRunsRef.current.set(rootWidgetId, { controller, widgetIds: busyWidgetIds });
      setRevalidatingWidgetIds(getBusyWidgetIds(cascadeRunsRef.current));

      const selections = new Map<string, unknown[]>();
      for (const tile of tileObjects) {
        const tileWidget = tile.item.widget;
        if (tileWidget.type === WidgetType.Filter) {
          const tileFilter = dashboardFilters.find((f) => f.id === tileWidget.dashboardFilterId);
          selections.set(
            tileWidget.widgetId,
            tileFilter === undefined ? (tileWidget.defaultValue ?? []) : getFilterWidgetValues(tileFilter, tileWidget)
          );
        }
      }
      overrides.forEach((values, id) => selections.set(id, values));

      try {
        await runFilterRevalidationCascade({
          levels,
          filterWidgets: widgets,
          filters: dashboardFilters,
          selections,
          signal: controller.signal,
          onWidgetValuesChange: (childWidgetId, nextValues) =>
            updateWidget(childWidgetId, { defaultValue: nextValues }),
        });
      } catch (error) {
        if (!controller.signal.aborted) {
          logError(error, "[useDashboardState] runFilterCascade");
        }
      } finally {
        if (!controller.signal.aborted) {
          cascadeRunsRef.current.delete(rootWidgetId);
          setRevalidatingWidgetIds(getBusyWidgetIds(cascadeRunsRef.current));
        }
      }
    },
    [dashboardFilters, tileObjects, updateWidget]
  );

  const revalidateChildFilters = useCallback(
    async (widgetId: string, values: unknown[]) => {
      const levels = getFilterWidgetDescendantLevels(filterWidgets, widgetId);
      if (levels.length === 0) {
        return;
      }
      await runFilterCascade(widgetId, levels, filterWidgets, new Map([[widgetId, values]]));
    },
    [filterWidgets, runFilterCascade]
  );

  const revalidateFilterTree = useCallback(
    async (
      widgetId: string,
      link: Pick<FilterWidgetSummary, "parentWidgetId" | "dashboardFilterId">,
      values: unknown[]
    ) => {
      const widgets = filterWidgets.map((w) => (w.widgetId === widgetId ? { ...w, ...link } : w));
      const widget = widgets.find((w) => w.widgetId === widgetId);
      if (widget === undefined) {
        return;
      }
      await runFilterCascade(
        widgetId,
        [[widget], ...getFilterWidgetDescendantLevels(widgets, widgetId)],
        widgets,
        new Map([[widgetId, values]])
      );
    },
    [filterWidgets, runFilterCascade]
  );

  const selectedWidget = useMemo(() => {
    return tileObjects.find((t) => t.id === selectedWidgetId)?.item?.widget;
  }, [tileObjects, selectedWidgetId]);

  return {
    tileObjects,
    dashboard,
    filters: dashboardFilters,
    loading: {
      inProgress: isLoading,
      error: loadingError,
    },
    updating: {
      inProgress: isSaving,
      error: saveError,
    },
    isEditMode,
    dashboardLoaded,
    save,
    saveDashboardName,
    addNewTile,
    updateTileLayout,
    updateTile,
    updateTileWidget,
    updateWidget,
    deleteTile,
    setIsEditMode,
    addFilters,
    filterWidgets,
    revalidatingWidgetIds,
    revalidateChildFilters,
    revalidateFilterTree,

    selectedWidget,
    setSelectedWidgetId,

    activeFilters,
    setActiveFilter,
    removeActiveFilter,

    createEditTileUrl,
  };
};

export default useDashboardState;

export type DashboardState = ReturnType<typeof useDashboardState>;
